import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import { getCurrentLanguage } from '../api/languageHelper';
import { fetchCrops, fetchDiseases, fetchSymptomsByLevel } from '../api/cropService';
import { useTranslation } from 'react-i18next';
import { getaccesstoken } from '../context/AuthContext';

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const AddProblem = () => {
  const { t } = useTranslation();
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [diseases, setDiseases] = useState([]);
  const [symptomsLevel1, setSymptomsLevel1] = useState([]);
  const [symptomsLevel2, setSymptomsLevel2] = useState([]);
  const [symptomsLevel3, setSymptomsLevel3] = useState([]);
  const [problemDetails, setProblemDetails] = useState({
    commonName: '',
    scientificName: '',
    description: '',
    chemicalAdvice: '',
    organicAdvice: ''
  });
  const [symptomFields, setSymptomFields] = useState([{
    symptomId: '',
    symptomName: '',
    question: '',
    l1SymptomId: '',
    l2SymptomId: '', 
    level: 2
  }]);

  // Fetch crops on initial load
  useEffect(() => {
    const loadCrops = async () => {
      try {
        const cropData = await fetchCrops(true);
        setCrops(cropData);
        if (cropData.length > 0) {
          const firstCrop = cropData[0];
          setSelectedCrop(firstCrop);
          loadDiseases(firstCrop.cid);
        }
      } catch (err) {
        console.error('Error fetching crops:', err);
      }
    };
    loadCrops();
  }, []);

  // Fetch diseases based on selected crop
  const loadDiseases = async (cropId) => {
    try {
      const diseaseData = await fetchDiseases(cropId, true);
      setDiseases(diseaseData);
    } catch (err) {
      console.error('Error fetching diseases:', err);
    }
  };

  // Fetch symptoms based on crop and level
  useEffect(() => {
    const loadSymptoms = async () => {
      if (selectedCrop) {
        const level1 = await fetchSymptomsByLevel(selectedCrop.cid, 1, true);
        setSymptomsLevel1(level1);
        const level2 = await fetchSymptomsByLevel(selectedCrop.cid, 2, true);
        setSymptomsLevel2(level2);
        const level3 = await fetchSymptomsByLevel(selectedCrop.cid, 3, true);
        setSymptomsLevel3(level3);
      }
    };
    loadSymptoms();
  }, [selectedCrop]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProblemDetails((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSymptomChange = (index, e) => {
    const { name, value } = e.target;
    const updatedSymptoms = [...symptomFields];
    updatedSymptoms[index][name] = value;
    // Detect if an existing symptom is selected
    if (name === 'symptomId') {
      if (value !== '') {
        // Mark as existing and clear other fields
        const selectedSymptom =
          [...symptomsLevel2, ...symptomsLevel3].find((s) => String(s.sid) === String(value));
        updatedSymptoms[index] = {
          ...updatedSymptoms[index],
          symptomId: value,
          isExisting: true,
          symptomName: '',
          question: '',
          l1SymptomId: '',
          l2SymptomId: '',
          level: selectedSymptom ? selectedSymptom.level : 2 // ✅ use actual level
        };
      } else {
        // If user clears dropdown, re-enable inputs
        updatedSymptoms[index].isExisting = false;
      }
    }
    // When level is 3 and user selects an L2 symptom, auto-fill its L1 parent
    if (name === 'l2SymptomId' && updatedSymptoms[index].level === 3) {
      const selectedL2 = symptomsLevel2.find((s) => String(s.sid) === String(value));
      updatedSymptoms[index].l1SymptomId = selectedL2 ? selectedL2.parent_id : '';
    }
    setSymptomFields(updatedSymptoms);
  };

  const addSymptomField = () => {
    setSymptomFields([
      ...symptomFields,
      {
        symptomId: '',
        symptomName: '',
        question: '',
        l1SymptomId: '',
        l2SymptomId: '',
        isExisting: false,
        level: 2
      }
    ]);
  };

  const removeSymptomField = (index) => {
    if (symptomFields.length === 1) {
      return;
    }
    const updatedSymptoms = symptomFields.filter((_, i) => i !== index);
    setSymptomFields(updatedSymptoms);
  };

  const handleSubmit = async () => {
    try {
      if (!selectedCrop) {
        console.error('No crop selected');
        return;
      }

      const token = getaccesstoken();
      const headers = {
        'Content-Type': 'application/json',
        'auth-token': token
      };

      const locale = getCurrentLanguage();

      // 1) Create disease
      const diseasePayload = {
        disid: null,
        cid: selectedCrop.cid,
        chemicalName: problemDetails.commonName,
        scientificName: problemDetails.scientificName,
        chemicalAdvice: problemDetails.chemicalAdvice,
        organicAdvice: problemDetails.organicAdvice,
        ipmMeasures: problemDetails.ipmMeasures || '',
        locale_code: locale,
        description: problemDetails.description
      };

      const diseaseResp = await axiosInstance.post(`${apiPrefix}disease`, diseasePayload, { headers });
      const disId = diseaseResp.data?.disId;
      if (!disId) {
        console.error('disease creation response missing disId:', diseaseResp.data);
        return;
      }

      // 2) Identify which symptoms need creation
      const createPayload = [];
      const createIndexMap = [];
      const cropId = selectedCrop.cid;
      symptomFields.forEach((s, idx) => {
        if (!s.symptomId || s.symptomId === '') {
          const payload = {
            level: Number(s.level || 2),
            cid: String(cropId),
            locale_code: locale,
            name: s.symptomName || `Symptom ${idx + 1}`,
            description: s.description || s.symptomName,
            qn: s.question || ''
          };
          if (Number(payload.level) === 2 && s.l1SymptomId) {
            payload.parent_id = String(s.l1SymptomId);
          }
          if (Number(payload.level) === 3) {
            payload.parent_id = String(s.l2SymptomId);
            if (s.l1SymptomId) payload.grandparent_id = String(s.l1SymptomId);
          }
          createPayload.push(payload);
          createIndexMap.push(idx);
        }
      });

      // 3) Create new symptoms in batch if needed
      let createdMap = {};
      if (createPayload.length !== 0) {
        const resp1 = await axiosInstance.post(`${apiPrefix}symptom/array/post`, createPayload, { headers });
        const resp = resp1.data;
        resp.data.forEach((created, i) => {
          const fieldIndex = createIndexMap[i];
          createdMap[fieldIndex] = created.id;
        });
      }

      // 4) Merge newly created symptom IDs back
      const finalSymptomFields = symptomFields.map((s, idx) => {
        const copy = { ...s };
        if ((!copy.symptomId || copy.symptomId === '') && createdMap[idx]) {
          copy.symptomId = String(createdMap[idx]);
        }
        return copy;
      });

      // 5) Build disease-symptom linking payload and post to /disease/array/post
      const diseaseSymPayload = finalSymptomFields.map((s) => {
        return {
          disid: Number(disId),
          cid: Number(selectedCrop.cid),
          sid: Number(s.symptomId),
          level: Number(s.level)
        };
      });
      const missing = diseaseSymPayload.filter(p => !p.sid || isNaN(p.sid));
      if (missing.length > 0) {
        console.error('Some symptoms are missing sids, cannot link:', missing);
        return;
      }

      const linkResp = await axiosInstance.post(`${apiPrefix}disease/array/post`, diseaseSymPayload, { headers });
      if (!linkResp || !(linkResp.status >= 200 && linkResp.status < 300)) {
        console.error('Failed to link disease to symptoms. Response:', linkResp && (linkResp.data || linkResp.status));
      }

      alert(t('Problem Added successfully!'));
      // Update UI and refetch
      setDiseases(prev => [
        ...prev,
        {
          disId,
          chemical_name: problemDetails.commonName,
          scientific_name: problemDetails.scientificName,
          description: problemDetails.description
        }
      ]);
      await loadDiseases(selectedCrop.cid);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || err?.message || 'Unknown error';
      alert(t('Failed to Add Problem') + `: ${msg}`);
    } finally {
      // Reset form
      setProblemDetails({
        commonName: '',
        scientificName: '',
        description: '',
        chemicalAdvice: '',
        organicAdvice: ''
      });

      setSymptomFields([{
        symptomId: '',
        symptomName: '',
        question: '',
        l1SymptomId: '',
        l2SymptomId: '',
        level: 2
      }]);
    }
  };


  return (
    <div className="flex h-screen">
      {/* LEFT: Add Problem Form */}
      <div className="w-2/3 p-6 overflow-y-auto bg-gray-50">
        <h1 className="text-2xl font-bold mb-4 text-gray-800">{t('expert.addProblem.pageTitle')}</h1>

        {/* Problem Details Form */}
        <div className="mb-6">
          <div className="mb-4">
            <label htmlFor="commonName" className="block font-semibold text-gray-700">{t('expert.addProblem.problemCommonNameLabel')}</label>
            <input
              type="text"
              id="commonName"
              name="commonName"
              value={problemDetails.commonName}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="scientificName" className="block font-semibold text-gray-700">{t('expert.addProblem.problemScientificNameLabel')}</label>
            <input
              type="text"
              id="scientificName"
              name="scientificName"
              value={problemDetails.scientificName}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="description" className="block font-semibold text-gray-700">{t('expert.addProblem.problemDescriptionLabel')}</label>
            <textarea
              id="description"
              name="description"
              value={problemDetails.description}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="chemicalAdvice" className="block font-semibold text-gray-700">{t('expert.addProblem.chemicalAdviceLabel')}</label>
            <input
              type="text"
              id="chemicalAdvice"
              name="chemicalAdvice"
              value={problemDetails.chemicalAdvice}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="organicAdvice" className="block font-semibold text-gray-700">{t('expert.addProblem.organicAdviceLabel')}</label>
            <input
              type="text"
              id="organicAdvice"
              name="organicAdvice"
              value={problemDetails.organicAdvice}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
        </div>

        {/* Add Symptoms Section */}
        <h2 className="text-xl font-semibold mb-3">{t('expert.addProblem.symptomsSectionTitle')}</h2>
        {symptomFields.map((symptom, index) => (
          <div key={index} className="mb-4">
            <div className="mb-2">
              <label className="font-semibold text-gray-700">{t('expert.addProblem.selectSymptomLabel')}</label>
              <select
                name="symptomId"
                value={symptom.symptomId}
                onChange={(e) => handleSymptomChange(index, e)}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="">{t('expert.addProblem.selectSymptomPlaceholder')}</option>
                {[...symptomsLevel2, ...symptomsLevel3].map((s) => (
                  <option key={s.sid} value={s.sid}>
                    {s.name} {s.level ? `(L${s.level})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-2">
              <label className="font-semibold text-gray-700">{t('expert.addProblem.symptomNameLabel')}</label>
              <input
                type="text"
                name="symptomName"
                value={symptom.symptomName}
                onChange={(e) => handleSymptomChange(index, e)}
                disabled={symptom.isExisting}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="mb-2">
              <label className="font-semibold text-gray-700">{t('expert.addProblem.questionLabel')}</label>
              <input
                type="text"
                name="question"
                value={symptom.question}
                onChange={(e) => handleSymptomChange(index, e)}
                disabled={symptom.isExisting}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="mb-2">
              <label className="font-semibold text-gray-700">{t('expert.addProblem.symptomLevelLabel')}</label>
              <select
                name="level"
                value={symptom.level}
                onChange={(e) => handleSymptomChange(index, e)}
                disabled={symptom.isExisting}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value={2}>Level 2</option>
                <option value={3}>Level 3</option>
              </select>
            </div>
            {/* <div className="mb-2">
              <label className="font-semibold text-gray-700">{t('expert.addProblem.l1SymptomLabel')}</label>
              <select
                name="l1SymptomId"
                value={symptom.l1SymptomId}
                onChange={(e) => handleSymptomChange(index, e)}
                disabled={symptom.isExisting}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="">{t('expert.addProblem.selectL1SymptomPlaceholder')}</option>
                {symptomsLevel1.map((s) => (
                  <option key={s.id} value={s.sid}>{s.name}</option>
                ))}
              </select>
            </div> */}
            {/* Parent Selection */}
            {symptom.level === 2 ? (
              // 👇 for Level 2: show L1 parent dropdown
              <div className="mb-2">
                <label className="font-semibold text-gray-700">
                  {t('expert.addProblem.l1SymptomLabel')}
                </label>
                <select
                  name="l1SymptomId"
                  value={symptom.l1SymptomId}
                  onChange={(e) => handleSymptomChange(index, e)}
                  disabled={symptom.isExisting}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="">{t('expert.addProblem.selectL1SymptomPlaceholder')}</option>
                  {symptomsLevel1.map((s) => (
                    <option key={s.sid} value={s.sid}>{s.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              // for Level 3: show both L2 dropdown and auto L1 parent display
              <div className="mb-2">
                <label className="font-semibold text-gray-700">
                  {t('expert.addProblem.l2SymptomLabel')}
                </label>
                <select
                  name="l2SymptomId"
                  value={symptom.l2SymptomId}
                  onChange={(e) => handleSymptomChange(index, e)}
                  disabled={symptom.isExisting}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="">{t('expert.addProblem.selectL2SymptomPlaceholder')}</option>
                  {symptomsLevel2.map((s) => (
                    <option key={s.sid} value={s.sid}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            {symptomFields.length > 1 && (
              <button
                type="button"
                onClick={() => removeSymptomField(index)}
                className="text-red-500"
              >
                {t('expert.addProblem.removeSymptomButton')}
              </button>
            )}
            {/* <button type="button" onClick={() => removeSymptomField(index)} className="text-red-500">{t('expert.addProblem.removeSymptomButton')}</button> */}
          </div>
        ))}
        <button type="button" onClick={addSymptomField} className="text-blue-500">{t('expert.addProblem.addAnotherSymptomButton')}</button>

        <div className="mt-6">
          <button onClick={handleSubmit} className="w-full bg-blue-500 text-white p-2 rounded">{t('expert.addProblem.submitButton')}</button>
        </div>
      </div>

      {/* RIGHT: Existing Crops and Diseases */}
      <div className="w-1/3 bg-gray-100 p-6 h-screen overflow-y-auto">
        <h2 className="text-xl font-semibold mb-3">{t('expert.addProblem.existingCropsTitle')}</h2>
        <div className="space-y-3">
          {crops.map((crop) => (
            <div
              key={crop.cid}
              onClick={() => {
                setSelectedCrop(crop);
                loadDiseases(crop.cid);
              }}
              className={`p-3 cursor-pointer rounded ${selectedCrop?.cid === crop.cid ? 'bg-blue-200' : 'bg-white'}`}
            >
              {crop.name}
            </div>
          ))}
        </div>
        <h2 className="text-xl font-semibold mt-6 mb-3">{t('expert.addProblem.existingProblemsTitle')}</h2>
        <div>
          {diseases.map((disease) => (
            <div key={disease.disId} className="mb-2 p-2 bg-white rounded">{disease.chemical_name || disease.scientific_name}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddProblem;